import type { ReactNode } from 'react';

import { Box, Paper, Stack, Typography } from '@mui/material';

type GalleryHeaderProps = {
	title: string;
	subtitle: ReactNode;
	utilityActions?: ReactNode;
	primaryAction?: ReactNode;
	statistics?: ReactNode;
	search?: ReactNode;
	searchActions?: ReactNode;
};

/** Shared header layout for card-gallery views such as Apps and Services. */
export default function GalleryHeader({ title, subtitle, utilityActions, primaryAction, statistics, search, searchActions }: GalleryHeaderProps) {
	return (
		<Paper
			sx={{
				p: { xs: 1.5, sm: 2.5 },
				backgroundColor: 'background.header',
				backgroundImage: 'none',
				border: 0,
				boxShadow: 'none',
			}}
		>
			<Stack spacing={2.5}>
				<Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'flex-start' }}>
					<Box
						sx={{
							minWidth: 0,
							position: 'relative',
							py: 0.25,
							'&::before': {
								content: '""',
								position: 'absolute',
								top: '105%',
								left: -12,
								right: -12,
								height: { xs: 58, sm: 72 },
								backgroundImage: 'url("/assets/randol-stream.png")',
								backgroundRepeat: 'no-repeat',
								backgroundPosition: 'left center',
								backgroundSize: '100% 100%',
								transform: 'translateY(-50%)',
								opacity: 0.92,
								pointerEvents: 'none',
								zIndex: 0,
							},
						}}
					>
						<Typography component="h1" variant="h4" sx={{ fontSize: { xs: '1.35rem', sm: undefined }, fontWeight: 600, position: 'relative', zIndex: 1 }}>
							{title}
						</Typography>
						<Typography color="text.secondary" sx={{ position: 'relative', zIndex: 1 }}>
							{subtitle}
						</Typography>
					</Box>
					{(utilityActions || primaryAction) && (
						<Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end" sx={{ alignSelf: { xs: 'flex-end', sm: 'auto' }, flexShrink: 0 }}>
							{utilityActions}
							{primaryAction}
						</Stack>
					)}
				</Stack>
				{(statistics || search) && (
					<Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems={{ xs: 'stretch', lg: 'center' }}>
						{statistics && <Box sx={{ minWidth: 0, flexShrink: 0, overflowX: 'auto' }}>{statistics}</Box>}
						{search && (
							<Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ minWidth: 0, flexGrow: 1 }}>
								<Box sx={{ minWidth: 0, flexGrow: 1 }}>{search}</Box>
								{searchActions && <Box sx={{ alignSelf: { xs: 'flex-end', sm: 'auto' }, flexShrink: 0 }}>{searchActions}</Box>}
							</Stack>
						)}
					</Stack>
				)}
			</Stack>
		</Paper>
	);
}
